import type { PermissionRequest, Post, PostList, ProductTag } from "@brandwall/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { ApiError, api, engagement, patch, post, timeAgo } from "../api";
import { CheckNow } from "../components/CheckNow";
import { LicensedSong } from "../components/LicensedSong";
import { PlayVideo } from "../components/PlayVideo";
import { PostLabels } from "../components/PostLabels";
import { SavedVideo } from "../components/SavedVideo";
import { useWorkspace } from "../components/Shell";
import { SwipeDeck } from "../components/SwipeDeck";
import { useToast } from "../components/Toast";
import { RequestPanel, STATUS_LABEL } from "./Rights";

const FLAG_LABEL: Record<string, string> = {
  children: "May show children",
  competitor: "Competitor brand",
  screenshot: "Looks like a screenshot",
  dark: "Dark photo",
  blurry: "Blurry",
};

function Detail({ p }: { p: Post }) {
  const { ws } = useParams();
  const workspace = useWorkspace();
  const canAct = workspace.role !== "viewer";
  const qc = useQueryClient();
  const toast = useToast();
  const [request, setRequest] = useState<PermissionRequest | null>(null);
  const [who, setWho] = useState("");
  const [error, setError] = useState<string | null>(null);
  const tags = useQuery({
    queryKey: ["post-products", ws, p.id],
    queryFn: () => api<{ tags: ProductTag[] }>(`/v1/w/${ws}/posts/${p.id}/products`),
  });

  // The message is ready as soon as a post is opened (D-066). Nothing is asked, and nothing shows
  // in Rights, until it is copied and marked as sent.
  const [preparing, setPreparing] = useState(false);
  useEffect(() => {
    let current = true;
    setRequest(null);
    setError(null);
    setWho("");
    setPreparing(true);
    (async () => {
      try {
        const r = await api<{ request: PermissionRequest | null }>(
          `/v1/w/${ws}/posts/${p.id}/request`,
        );
        const req =
          r.request ??
          (canAct
            ? await post<PermissionRequest>(`/v1/w/${ws}/posts/${p.id}/ask`, { route: "dm" })
            : null);
        if (current) setRequest(req);
      } catch (e) {
        if (current) setError(e instanceof ApiError ? e.message : null);
      } finally {
        if (current) setPreparing(false);
      }
    })();
    return () => {
      current = false;
    };
  }, [p.id, ws, canAct]);

  const saveWho = async () => {
    if (!request || !who.trim()) return;
    try {
      setRequest(
        await patch<PermissionRequest>(`/v1/w/${ws}/requests/${request.id}`, { creator: who }),
      );
      qc.invalidateQueries({ queryKey: ["posts", ws, "discover"] });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That doesn't look like an Instagram username.");
    }
  };

  const save = async (saved: boolean) => {
    const r = await post<{ summary: string }>(`/v1/w/${ws}/posts/save`, { ids: [p.id], saved });
    toast(r.summary);
    qc.invalidateQueries({ queryKey: ["posts", ws] });
  };

  const skip = async () => {
    const r = await post<{ summary: string }>(`/v1/w/${ws}/posts/skip`, {
      ids: [p.id],
      reason: "Not interested",
    });
    toast(r.summary);
    qc.invalidateQueries({ queryKey: ["posts", ws] });
  };

  const product = tags.data?.tags[0];
  const granted = request?.status === "granted";
  const creator = request?.creator || p.creator;
  const kind =
    p.mediaType === "video" ? "Video" : p.mediaType === "carousel" ? "Carousel" : "Photo";
  return (
    <section className="card stack">
      {/* Everything needed to decide comes first; asking is the step after. */}
      <div className="ddetail">
        {p.previewUrl && (
          <div className="dpic">
            <a href={p.permalink ?? undefined} target="_blank" rel="noreferrer">
              <img
                src={p.previewUrl}
                alt={p.caption ? `Post: ${p.caption.slice(0, 80)}` : "Post"}
              />
            </a>
            {p.mediaType !== "image" && <span className="dkind">{kind}</span>}
            <PlayVideo post={p} />
          </div>
        )}
        <div className="stack" style={{ gap: 8, minWidth: 0 }}>
          <div className="row wrap" style={{ gap: 8 }}>
            <b>{creator ? `@${creator}` : "Unknown creator"}</b>
            {request && (
              <span className={`status ${request.status}`}>{STATUS_LABEL[request.status]}</span>
            )}
          </div>
          <span className="small muted">
            {p.hashtag ? `via #${p.hashtag} · ` : ""}posted {timeAgo(p.postedAt)} ·{" "}
            {kind.toLowerCase()}
            {workspace.showEngagement && engagement(p) ? ` · ${engagement(p)}` : ""}
            {p.permalink && (
              <>
                {" · "}
                <a href={p.permalink} target="_blank" rel="noreferrer">
                  Open on Instagram ↗
                </a>
              </>
            )}
          </span>
          {p.score !== null && (
            <div className="dscore">
              <b>{p.score}</b>
              <div className="small">
                {p.scoreReason ?? "Score from your Taste"}
                {p.scores && (
                  <div className="muted">
                    Quality {p.scores.quality} · Product {p.scores.product} · Brand fit{" "}
                    {p.scores.brandFit}
                  </div>
                )}
              </div>
            </div>
          )}
          {(p.topPick || p.flags.length > 0 || p.audioType === "MUSIC") && (
            <div className="row wrap" style={{ gap: 4 }}>
              {p.topPick && <span className="pill ok">Top pick</span>}
              <LicensedSong post={p} />
              {p.flags
                .filter((f) => FLAG_LABEL[f])
                .map((f) => (
                  <span key={f} className="pill berry">
                    {FLAG_LABEL[f]}
                  </span>
                ))}
            </div>
          )}
          <SavedVideo post={p} />
          {product && (
            <span className="small">
              Looks like: <b>{product.title}</b>
              {product.confidence ? ` · ${product.confidence}% match` : ""}
            </span>
          )}
        </div>
      </div>
      {p.caption && <p className="dcap small">{p.caption}</p>}
      <PostLabels p={p} canAct={canAct} />

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      {granted && (
        <div className="notice" role="status">
          <b>{request!.creator ? `@${request!.creator}` : "The creator"} said yes.</b> The post is
          on your walls now. You'll find it under <Link to={`/w/${ws}/inbox?tab=live`}>Live</Link>.
        </div>
      )}

      {canAct && !request && preparing && <p className="small muted">Writing the message…</p>}

      {canAct && request?.status === "draft" && (
        <p className="small muted">
          Copy the message, send it to the creator on Instagram, and mark it as sent. When they say
          yes, the post goes on your walls. Nothing is sent until you do.
        </p>
      )}

      {request && !request.creator && canAct && (
        <div className="stack" style={{ gap: 8 }}>
          <p className="small">
            Instagram doesn't tell us who posted this. Open the post and add their username, so your
            message greets them by name. If you skip this, they fill it in when they answer.
          </p>
          <div className="row">
            <input
              className="input"
              value={who}
              onChange={(e) => setWho(e.target.value)}
              placeholder="@username"
              aria-label="Instagram username"
            />
            <button
              type="button"
              className="btn secondary"
              onClick={saveWho}
              disabled={!who.trim()}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {request && (
        <RequestPanel
          compact
          request={request}
          onChange={(r) => {
            setRequest(r);
            qc.invalidateQueries({ queryKey: ["rights", ws] });
            qc.invalidateQueries({ queryKey: ["posts", ws] });
          }}
        />
      )}

      {canAct && !granted && (
        <div className="row" style={{ gap: 12 }}>
          <button type="button" className="btn link small" onClick={() => save(!p.saved)}>
            {p.saved ? "♥ Saved · take off the list" : "♥ Save to ask later"}
          </button>
          <button type="button" className="btn link small" onClick={skip}>
            Ignore
          </button>
        </div>
      )}
    </section>
  );
}

/** A post left out of Discover: why, and a way to bring it back. */
function IgnoredDetail({ p, onBack }: { p: Post; onBack: (ids: string[]) => void }) {
  const workspace = useWorkspace();
  return (
    <section className="card stack">
      <div className="row" style={{ alignItems: "flex-start" }}>
        {p.previewUrl && (
          <img
            src={p.previewUrl}
            alt={p.caption ? `Post: ${p.caption.slice(0, 80)}` : "Post"}
            style={{ width: 150, borderRadius: 12, aspectRatio: "4/5", objectFit: "cover" }}
          />
        )}
        <div className="stack spacer" style={{ gap: 6 }}>
          <b>{p.creator ? `@${p.creator}` : "Unknown creator"}</b>
          <span className="small muted">
            {p.hashtag ? `via #${p.hashtag} · ` : ""}posted {timeAgo(p.postedAt)}
            {p.permalink && (
              <>
                {" · "}
                <a href={p.permalink} target="_blank" rel="noreferrer">
                  Instagram ↗
                </a>
              </>
            )}
          </span>
          <span className="small">
            <b>{p.autoSkipped ? "Left out by Brandwall:" : "Ignored:"}</b>{" "}
            {p.skipReason ?? "Not interested"}
          </span>
          {p.caption && (
            <p className="small muted" style={{ maxHeight: 64, overflow: "hidden" }}>
              {p.caption}
            </p>
          )}
        </div>
      </div>
      {workspace.role !== "viewer" && (
        <button
          type="button"
          className="btn secondary"
          style={{ justifySelf: "start" }}
          onClick={() => onBack([p.id])}
        >
          Bring back to Discover
        </button>
      )}
    </section>
  );
}

export function Discover() {
  const { ws } = useParams();
  const workspace = useWorkspace();
  const canAct = workspace.role !== "viewer";
  const qc = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const ignoredView = params.get("view") === "ignored";
  const tab = ignoredView ? "ignored" : "discover";
  const sort = (params.get("sort") as "best" | "likes" | "newest" | null) ?? "best";
  const swipe = !ignoredView && params.get("mode") === "swipe";
  const savedOnly = !ignoredView && params.get("saved") === "1";
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const list = useQuery({
    queryKey: ["posts", ws, tab, sort, savedOnly],
    queryFn: () =>
      api<PostList>(
        `/v1/w/${ws}/posts?tab=${tab}&sort=${sort}&limit=60${savedOnly ? "&saved=true" : ""}`,
      ),
  });
  const posts = list.data?.posts ?? [];
  const selectedId = params.get("p") ?? posts[0]?.id;
  const selected = posts.find((p) => p.id === selectedId) ?? posts[0];
  const shown = posts.filter((p) => checked.has(p.id));
  const allChecked = posts.length > 0 && shown.length === posts.length;

  // biome-ignore lint/correctness/useExhaustiveDependencies: a new list starts with nothing selected
  useEffect(() => setChecked(new Set()), [tab]);

  const toggle = (id: string, on: boolean) =>
    setChecked((s) => {
      const n = new Set(s);
      on ? n.add(id) : n.delete(id);
      return n;
    });

  const run = async (path: "skip" | "restore", ids: string[]) => {
    if (!ids.length || busy) return;
    setBusy(true);
    try {
      const r = await post<{ summary: string }>(`/v1/w/${ws}/posts/${path}`, {
        ids,
        ...(path === "skip" ? { reason: "Not interested" } : {}),
      });
      toast(r.summary);
      setChecked(new Set());
      await qc.invalidateQueries({ queryKey: ["posts", ws] });
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };
  const view = (v: "found" | "ignored") =>
    setParams(v === "ignored" ? { view: "ignored" } : {}, { replace: true });
  const setSort = (v: string) => {
    const next = new URLSearchParams(params);
    if (v === "best") next.delete("sort");
    else next.set("sort", v);
    next.delete("p");
    setParams(next, { replace: true });
  };
  const counts = list.data?.counts;
  const found = list.data?.found;
  const setParam = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v === null) next.delete(k);
    else next.set(k, v);
    next.delete("p");
    setParams(next, { replace: true });
  };

  return (
    <div className={swipe ? "stack" : "split wide-side"}>
      <section className="stack" style={{ minWidth: 0 }}>
        <div>
          <h2>Found for you</h2>
          <p className="muted" hidden={swipe}>
            Good posts with your hashtags, from people who didn't tag you. Best first. Ask a creator
            for permission, and the post goes on your walls when they say yes. Posts that tag or
            mention you are in the <Link to={`/w/${ws}/inbox`}>Inbox</Link>.
          </p>
        </div>
        <div className="tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={!ignoredView}
            className={`tab ${!ignoredView ? "on" : ""}`}
            onClick={() => view("found")}
          >
            Found
            {!!counts?.discover && <span className="c">{counts.discover}</span>}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={ignoredView}
            className={`tab ${ignoredView ? "on" : ""}`}
            onClick={() => view("ignored")}
          >
            Ignored
            {!!counts?.ignored && <span className="c">{counts.ignored}</span>}
          </button>
        </div>
        {ignoredView ? (
          <p className="small muted">
            Posts you ignored, and weak ones Brandwall left out by itself, with the reason.
          </p>
        ) : (
          <>
            <CheckNow />
            {list.data?.waiting &&
              (list.data.waiting.preparing > 0 || list.data.waiting.noPicture > 0) && (
                <p className="small muted">
                  {list.data.waiting.preparing > 0 &&
                    `${list.data.waiting.preparing} new ${list.data.waiting.preparing === 1 ? "post is" : "posts are"} being prepared: Brandwall gets the picture and checks it against your Taste. `}
                  {list.data.waiting.noPicture > 0 &&
                    `${list.data.waiting.noPicture} ${list.data.waiting.noPicture === 1 ? "post is" : "posts are"} hidden because Instagram doesn't share the picture (usually videos).`}
                </p>
              )}
          </>
        )}
        {!swipe && list.isSuccess && posts.length === 0 && (
          <div className="card">
            <p className="muted">
              {ignoredView ? (
                "Nothing ignored."
              ) : savedOnly ? (
                "No saved posts. Go through posts one by one and press ♥ on the ones you want."
              ) : (
                <>
                  Nothing yet. Add a few hashtags on <Link to={`/w/${ws}/sources`}>Sources</Link>,
                  and the best posts that use them show up here.
                </>
              )}
            </p>
          </div>
        )}
        {!ignoredView && (
          <div className="row wrap" style={{ gap: 8 }}>
            <div className="tabs" role="group" aria-label="How to go through posts">
              <button
                type="button"
                className={`tab ${!swipe ? "on" : ""}`}
                aria-pressed={!swipe}
                onClick={() => setParam("mode", null)}
              >
                List
              </button>
              <button
                type="button"
                className={`tab ${swipe ? "on" : ""}`}
                aria-pressed={swipe}
                onClick={() => {
                  const next = new URLSearchParams(params);
                  next.set("mode", "swipe");
                  next.delete("saved");
                  next.delete("p");
                  setParams(next, { replace: true });
                }}
              >
                One by one
              </button>
            </div>
            {!swipe && (
              <div className="tabs" role="group" aria-label="Which posts">
                <button
                  type="button"
                  className={`tab ${!savedOnly ? "on" : ""}`}
                  aria-pressed={!savedOnly}
                  onClick={() => setParam("saved", null)}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`tab ${savedOnly ? "on" : ""}`}
                  aria-pressed={savedOnly}
                  onClick={() => setParam("saved", "1")}
                >
                  ♥ Saved{found?.saved ? <span className="c">{found.saved}</span> : null}
                </button>
              </div>
            )}
          </div>
        )}
        {swipe && (
          <SwipeDeck sort={sort} onShowSaved={() => setParams({ saved: "1" }, { replace: true })} />
        )}
        {!swipe && posts.length > 0 && (
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <select
              className="input"
              style={{ width: "auto" }}
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              aria-label="Sort posts"
            >
              <option value="best">Best first</option>
              <option value="likes">Most likes and comments</option>
              <option value="newest">Newest first</option>
            </select>
          </div>
        )}
        {!swipe && canAct && posts.length > 0 && (
          <div className="dtools">
            <label className="row" style={{ gap: 8 }}>
              <input
                type="checkbox"
                checked={allChecked}
                onChange={(e) =>
                  setChecked(e.target.checked ? new Set(posts.map((p) => p.id)) : new Set())
                }
              />
              <span className="small">
                {shown.length ? `${shown.length} selected` : "Select all"}
              </span>
            </label>
            {shown.length > 0 && (
              <>
                <button
                  type="button"
                  className="btn secondary sm"
                  onClick={() =>
                    run(
                      ignoredView ? "restore" : "skip",
                      shown.map((p) => p.id),
                    )
                  }
                  disabled={busy}
                >
                  {ignoredView ? "Bring back" : "Ignore"}{" "}
                  {shown.length === 1 ? "1 post" : `${shown.length} posts`}
                </button>
                <button
                  type="button"
                  className="btn link small"
                  onClick={() => setChecked(new Set())}
                >
                  Clear
                </button>
              </>
            )}
          </div>
        )}
        <div className="discover-list" hidden={swipe}>
          {posts.map((p) => (
            <div key={p.id} className="drow">
              {canAct && (
                <input
                  type="checkbox"
                  checked={checked.has(p.id)}
                  aria-label={`Select post by ${p.creator ? `@${p.creator}` : "unknown creator"}`}
                  onChange={(e) => toggle(p.id, e.target.checked)}
                />
              )}
              <button
                type="button"
                className="dcard"
                aria-pressed={p.id === selected?.id}
                onClick={() =>
                  setParams(ignoredView ? { view: "ignored", p: p.id } : { p: p.id }, {
                    replace: true,
                  })
                }
              >
                {p.previewUrl ? (
                  <img className="thumb" src={p.previewUrl} alt="" />
                ) : (
                  <span className="thumb" />
                )}
                <span style={{ minWidth: 0 }}>
                  <b style={{ display: "block" }}>
                    {p.saved && (
                      <span style={{ color: "var(--ok)" }} title="Saved to ask later">
                        ♥{" "}
                      </span>
                    )}
                    {p.creator ? `@${p.creator}` : "Unknown creator"}
                  </b>
                  <span className="small muted">
                    {ignoredView
                      ? (p.skipReason ?? "Not interested")
                      : `${p.hashtag ? `via #${p.hashtag} · ` : ""}${timeAgo(p.postedAt)}`}
                  </span>
                  {workspace.showEngagement && engagement(p) && (
                    <span className="small muted" style={{ display: "block" }}>
                      {engagement(p)}
                    </span>
                  )}
                </span>
                <span className="sc">
                  {p.permission === "pending" ? (
                    <span className="status pending">Asked</span>
                  ) : (
                    (p.score ?? "")
                  )}
                </span>
              </button>
            </div>
          ))}
        </div>
      </section>
      <div className="sticky-side">
        {selected &&
          !swipe &&
          (ignoredView ? (
            <IgnoredDetail p={selected} onBack={(ids) => run("restore", ids)} />
          ) : (
            <Detail p={selected} />
          ))}
      </div>
    </div>
  );
}
